import { useId, useState } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { PauseIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAskSarahActions, useSignedInAccount } from '../../../contexts/AccountContext';
import { founder } from '../../../data/content';
import { usePages, useSite } from '../../../contexts/SiteContext';
import { btnPrimary, btnQuiet, btnSecondary, chipCls } from '../../../ui/styles';
import { ease } from '../../../core/format/motion';
import { STOP_REASONS, STOP_TEXT_MAX, stopReasonFor, stopReasonLabel, type CustomerStopReason } from '../../../core/rules/stopReasons';

/**
 * Stoppen kan altijd, maar eerst één vraag: waarom? Soms past pauzeren of
 * een berichtje aan Sarah beter. Stopredenen komen uit Paginateksten.
 */
export function SubscriptionStop({
  active
}: {
  active: boolean;
}) {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    subscription
  } = useSignedInAccount();
  const {
    openAsk
  } = useAskSarahActions();
  const reduce = useReducedMotion();
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<CustomerStopReason | null>(null);
  const [other, setOther] = useState('');
  const [busy, setBusy] = useState(false);
  const {
    pauseOptions
  } = policy.subscription;
  const suggestedPause = pauseOptions[1] ?? pauseOptions[0];
  const suggest = STOP_REASONS.find(r => r.code === reason)?.suggest ?? 'geen';
  const pause = async () => {
    if (!suggestedPause) return;
    setBusy(true);
    const ok = await subscription({
      type: 'pause',
      weeks: suggestedPause.weeks
    });
    setBusy(false);
    if (!ok) return;
    setOpen(false);
    toast.success(`Gepauzeerd voor ${suggestedPause.label}`);
  };
  const stop = async () => {
    setBusy(true);
    const ok = await subscription({
      type: 'cancel',
      reason: stopReasonFor(reason, other)
    });
    setBusy(false);
    // Mislukt: het paneel blijft open, zodat ze het opnieuw kan proberen.
    if (!ok) return;
    setOpen(false);
    toast('Je abonnement is gestopt', {
      description: 'Er komen geen leveringen meer. Opnieuw starten kan altijd hier.'
    });
  };
  return <div className="px-1" data-id="element-6190" data-mp="eiop5ip7c82b">
      <button type="button" aria-expanded={open} aria-controls={panelId} onClick={() => setOpen(v => !v)} className={`${btnQuiet} -ml-3 text-muted`} data-id="element-6191" data-mp="s1r7sr9mm7a">
        Abonnement stoppen
      </button>
      <AnimatePresence initial={false} data-id="element-6192" data-mp="3my8jkl1lzwiy">
        {open && <motion.div id={panelId} initial={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} animate={reduce ? {
        opacity: 1
      } : {
        height: 'auto',
        opacity: 1
      }} exit={reduce ? {
        opacity: 0
      } : {
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.22,
        ease
      }} className="overflow-hidden" data-id="element-6193" data-mp="3ry37qgu0280b">
            <div className="mt-4 bg-shell p-5 ring-1 ring-line" data-id="element-6194" data-mp="1c5468jdpv3pc">
              <fieldset data-id="element-6195" data-mp="1g7isldglglld">
                <legend className="text-[15px] text-ink" data-id="element-6196" data-mp="1g6pt8xjysyd9">Jammer. Mag ik vragen waarom?</legend>
                <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-6197" data-mp="30r4cex08n39y">
                  {STOP_REASONS.map(r => <button key={r.code} type="button" aria-pressed={reason === r.code} onClick={() => setReason(current => current === r.code ? null : r.code)} className={`${chipCls(reason === r.code)} whitespace-normal text-left`} data-id="element-6198" data-mp="2wn6g0u2as6kl">
                      {stopReasonLabel(r.code, pages.portal.stopReasons)}
                    </button>)}
                </div>
                {reason === 'anders' && <div className="mt-3" data-id="element-6199" data-mp="3ohi9x31ldubx">
                    <label htmlFor={`${panelId}-other`} className="block text-[13px] text-muted" data-id="element-6200" data-mp="3oecl7owvkugq">
                      Wil je er iets over kwijt? <span className="text-muted" data-id="element-6201" data-mp="1u2o0t77vjzoy">(hoeft niet)</span>
                    </label>
                    <input id={`${panelId}-other`} value={other} onChange={e => setOther(e.target.value)} maxLength={STOP_TEXT_MAX} className="mt-1.5 w-full rounded-sm border border-line bg-white px-3 py-2.5 text-[16px] text-ink focus:border-ink focus:outline-none sm:text-[14px]" data-id="element-6202" data-mp="1whco8pvan644" />
                  </div>}
              </fieldset>

              <div aria-live="polite" data-id="element-6203" data-mp="1cr9108orlrmt">
                {suggest === 'pauze' && active && suggestedPause && <p className="mt-4 bg-sand p-3 text-[14px] leading-relaxed text-ink" data-id="element-6204" data-mp="1mr9nu4luepvr">
                    Dan past pauzeren misschien beter: je houdt je ritme en cadeau-teller.{' '}
                    <button type="button" disabled={busy} onClick={() => void pause()} className="inline-flex items-center gap-1 text-clay underline underline-offset-4" data-id="element-6205" data-mp="1rjt110gmbj01">
                      <PauseIcon className="h-3.5 w-3.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6206" data-mp="z048rrnq81mi" />
                      Pauzeer {suggestedPause.label}
                    </button>
                  </p>}
                {suggest === 'sarah' && <p className="mt-4 bg-sand p-3 text-[14px] leading-relaxed text-ink" data-id="element-6207" data-mp="hjgrwob9yoha">
                    Dat wil ik graag even zien, voordat je stopt. Vaak ligt het aan de volgorde of het ritme.{' '}
                    <button type="button" onClick={() => openAsk('reactie')} aria-haspopup="dialog" className="text-clay underline underline-offset-4" data-id="element-6208" data-mp="8jjzcblrl5gy">
                      Stuur {founder.firstName} een berichtje
                    </button>
                  </p>}
              </div>

              {/* Vóór de knop: wat stoppen betekent, zodat het geen verrassing is. */}
              <p className="mt-5 text-[13px] leading-relaxed text-muted" data-id="element-6209" data-mp="3l18fzrmaioa7">
                Na stoppen komen er geen leveringen meer en vervalt je cadeau-teller. Je bestellingen en huidplan blijven gewoon staan.
              </p>
              <div className="mt-3 flex flex-wrap gap-2" data-id="element-6210" data-mp="36cyr0ekkzict">
                <button type="button" disabled={busy} onClick={() => setOpen(false)} className={btnPrimary} data-id="element-6211" data-mp="3bd32fq6mtmw6">
                  Toch houden
                </button>
                {/* Opzeggen moet net zo makkelijk als aanmelden: een reden is welkom, nooit verplicht. */}
                <button type="button" disabled={busy} aria-busy={busy} onClick={() => void stop()} className={btnSecondary} data-id="element-6212" data-mp="32eithgqvirl5">
                  {busy ? 'Stoppen…' : 'Stoppen'}
                </button>
              </div>
              {!reason && <p className="mt-2 text-[13px] text-muted" data-id="element-6213" data-mp="3gcfq2d5buxzt">Een reden kiezen hoeft niet, maar helpt me wel.</p>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}